import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  ApiError,
  Testimonial,
  CreateTestimonialData,
  TestimonialsQueryParams,
  Contact,
  CreateContactData,
  Education,
  Experience,
  Certification,
  Skill,
  HealthCheck,
  PaginatedResponse,
  Language,
} from '../types/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/v1';

// Map language codes to Accept-Language header format
const LANGUAGE_MAP: Record<Language, string> = {
  pt: 'pt-BR',
  en: 'en-US',
  es: 'es-ES',
};

class ApiClient {
  private client: AxiosInstance;
  private currentLanguage: Language = 'pt';

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add language header
    this.client.interceptors.request.use(
      (config) => {
        const lang = LANGUAGE_MAP[this.currentLanguage] || 'pt-BR';
        config.headers['Accept-Language'] = lang;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const apiError: ApiError = {
          message: error.message,
          status: error.response?.status,
          code: error.code,
          details: error.response?.data,
        };

        // Handle specific error cases
        if (error.response?.status === 429) {
          apiError.message = 'Rate limit exceeded. Please try again later.';
        } else if (error.response?.status === 404) {
          apiError.message = 'Resource not found.';
        } else if (error.response?.status === 500) {
          apiError.message = 'Internal server error. Please try again later.';
        } else if (!error.response) {
          apiError.message = 'Network error. Please check your connection.';
        }

        return Promise.reject(apiError);
      }
    );
  }

  // Set current language for API requests
  setLanguage(language: Language): void {
    this.currentLanguage = language;
  }

  // Health Check
  async healthCheck(): Promise<HealthCheck> {
    const response = await this.client.get<HealthCheck>('/health');
    return response.data;
  }

  // Testimonials
  async getTestimonials(
    params?: TestimonialsQueryParams
  ): Promise<PaginatedResponse<Testimonial>> {
    const response = await this.client.get<PaginatedResponse<Testimonial>>('/testimonials', {
      params,
    });
    return response.data;
  }

  async createTestimonial(data: CreateTestimonialData): Promise<Testimonial> {
    const response = await this.client.post<Testimonial>('/testimonials', data);
    return response.data;
  }

  async deleteTestimonial(id: string): Promise<void> {
    await this.client.delete(`/testimonials/${id}`);
  }

  // Contacts
  async getContacts(params?: { page?: number; limit?: number }): Promise<PaginatedResponse<Contact>> {
    const response = await this.client.get<PaginatedResponse<Contact>>('/contacts', {
      params,
    });
    return response.data;
  }

  async createContact(data: CreateContactData): Promise<Contact> {
    const response = await this.client.post<Contact>('/contacts', data);
    return response.data;
  }

  async deleteContact(id: string): Promise<void> {
    await this.client.delete(`/contacts/${id}`);
  }

  // Education
  async getEducation(params?: { page?: number; limit?: number }): Promise<PaginatedResponse<Education>> {
    const response = await this.client.get<PaginatedResponse<Education>>('/education', {
      params,
    });
    return response.data;
  }

  async getEducationById(id: string): Promise<Education> {
    const response = await this.client.get<Education>(`/education/${id}`);
    return response.data;
  }

  // Experiences
  async getExperiences(params?: { page?: number; limit?: number }): Promise<PaginatedResponse<Experience>> {
    const response = await this.client.get<PaginatedResponse<Experience>>('/experiences', {
      params,
    });
    return response.data;
  }

  async getExperienceById(id: string): Promise<Experience> {
    const response = await this.client.get<Experience>(`/experiences/${id}`);
    return response.data;
  }

  // Certifications
  async getCertifications(params?: { page?: number; limit?: number }): Promise<PaginatedResponse<Certification>> {
    const response = await this.client.get<PaginatedResponse<Certification>>('/certifications', {
      params,
    });
    return response.data;
  }

  async getCertificationById(id: string): Promise<Certification> {
    const response = await this.client.get<Certification>(`/certifications/${id}`);
    return response.data;
  }

  // Skills
  async getSkills(params?: { page?: number; limit?: number }): Promise<PaginatedResponse<Skill>> {
    const response = await this.client.get<PaginatedResponse<Skill>>('/skills', {
      params,
    });
    return response.data;
  }

  async getSkillById(id: string): Promise<Skill> {
    const response = await this.client.get<Skill>(`/skills/${id}`);
    return response.data;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export individual service methods for easier imports
export const api = {
  // Health
  healthCheck: () => apiClient.healthCheck(),

  // Testimonials
  testimonials: {
    getAll: (params?: TestimonialsQueryParams) => apiClient.getTestimonials(params),
    create: (data: CreateTestimonialData) => apiClient.createTestimonial(data),
    delete: (id: string) => apiClient.deleteTestimonial(id),
  },

  // Contacts
  contacts: {
    getAll: (params?: { page?: number; limit?: number }) => apiClient.getContacts(params),
    create: (data: CreateContactData) => apiClient.createContact(data),
    delete: (id: string) => apiClient.deleteContact(id),
  },

  // Education
  education: {
    getAll: (params?: import('../types/api').EducationQueryParams) => apiClient.getEducation(params),
    getById: (id: string) => apiClient.getEducationById(id),
  },

  experiences: {
    getAll: (params?: import('../types/api').ExperienceQueryParams) => apiClient.getExperiences(params),
    getById: (id: string) => apiClient.getExperienceById(id),
  },

  certifications: {
    getAll: (params?: import('../types/api').CertificationQueryParams) => apiClient.getCertifications(params),
    getById: (id: string) => apiClient.getCertificationById(id),
  },

  // Skills
  skills: {
    getAll: (params?: { page?: number; limit?: number }) => apiClient.getSkills(params),
    getById: (id: string) => apiClient.getSkillById(id),
  },

  // Language setter
  setLanguage: (language: Language) => apiClient.setLanguage(language),
};

export default api;
